import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";

const AddDesignation = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "designation"
  );

  const [designationName, setDesignationName] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const canViewPage = hasModulePermission("designation.pages.create.view");
  const canSubmit =
    hasModulePermission("designation.actions.create") &&
    hasModulePermission("designation.actions.submit_create");
  const canOpenList =
    hasModulePermission("designation.pages.list.view") &&
    hasModulePermission("designation.actions.open_list");
  const canOpenTrashed =
    hasModulePermission("designation.pages.trashed.view") &&
    hasModulePermission("designation.actions.open_trashed");

  useEffect(() => {
    if (isAuthReady) setPageLoading(false);
  }, [isAuthReady]);

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create designations.",
        color: "error",
      });
      return;
    }

    if (!designationName.trim()) {
      setSnackbar({ open: true, message: "Designation name is required.", color: "error" });
      return;
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/designations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: designationName.trim() }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Error creating designation");

      setSnackbar({
        open: true,
        message: data.message || "Designation added successfully!",
        color: "success",
      });
      setTimeout(() => navigate("/designations"), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Something went wrong", color: "error" });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress size={60} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Add Designation
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to add designations.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Designation"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />
      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Add Designation
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenList && (
              <SoftButton
                size="small"
                color="info"
                onClick={() => navigate("/designations")}
                startIcon={<Icon>list</Icon>}
              >
                Designation List
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                onClick={() => navigate("/designations/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trashed List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <form onSubmit={handleSubmit}>
          {canViewField("name") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%", md: "20%" } }}>
              <SoftInput
                size="small"
                placeholder="Designation Name"
                value={designationName}
                onChange={(e) => setDesignationName(e.target.value)}
                required
              />
            </SoftBox>
          )}
          {canSubmit && (
            <SoftButton
              type="submit"
              color="info"
              size="small"
              disabled={submitLoading}
              endIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {submitLoading ? "Processing..." : "Submit Designation"}
            </SoftButton>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default AddDesignation;
